import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Filter, BookOpen, Megaphone, Eye, Calendar, Target, DollarSign, X, QrCode, Upload, Camera, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

type ContentType = 'book' | 'campaign';
type ContentFilter = 'all' | 'book' | 'campaign';

interface Book {
  id: string;
  title: string;
  isbn?: string;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  cover_url?: string;
  author_id?: string;
  type: 'book';
}

interface Campaign {
  id: string;
  title: string;
  description?: string;
  status: 'active' | 'paused' | 'completed' | 'draft';
  start_date?: string;
  end_date?: string;
  target_audience?: string;
  created_at: string;
  updated_at: string;
  total_scans?: number;
  total_clicks?: number;
  conversion_rate?: number;
  author_id?: string;
  type: 'campaign';
}

type ContentItem = (Book | Campaign) & { type: ContentType };

const Content: React.FC = () => {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { 
    checkBookLimitAndRedirect, 
    checkCampaignLimitAndRedirect,
    canCreateBook, 
    canCreateCampaign,
    getBookUsageInfo,
    getCampaignUsageInfo 
  } = usePlanLimits();
  
  const [content, setContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [contentFilter, setContentFilter] = useState<ContentFilter>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [createType, setCreateType] = useState<ContentType>('book');
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [selectedCover, setSelectedCover] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [sortField, setSortField] = useState<'title' | 'status' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  // Upload cover image for a book
  const uploadCoverForBook = async (bookId: string, file: File): Promise<string | null> => {
    if (!session?.access_token) return null;

    try {
      setUploadingCover(true);
      
      const formData = new FormData();
      formData.append('cover', file);
      
      console.log('📸 Uploading cover for book:', bookId);

      const response = await fetch(`${API_BASE}/api/cover/upload/${bookId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Hochladen des Covers');
      }

      const result = await response.json();
      console.log('✅ Cover uploaded:', result.url);
      return result.url;
    } catch (err) {
      console.error('❌ Failed to upload cover:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Hochladen des Covers');
      return null;
    } finally {
      setUploadingCover(false);
    }
  };

  // Load all content
  const loadContent = async () => {
    if (!session?.access_token) return;

    try {
      // Load books
      const booksResponse = await fetch(`${API_BASE}/api/books`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      // Load campaigns
      const campaignsResponse = await fetch(`${API_BASE}/api/campaigns`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (booksResponse.ok && campaignsResponse.ok) {
        const booksData = await booksResponse.json();
        const campaignsData = await campaignsResponse.json();
        
        // Add type to each item
        const books = booksData.map((book: Book) => ({ ...book, type: 'book' as ContentType }));
        const campaigns = campaignsData.map((campaign: Campaign) => ({ ...campaign, type: 'campaign' as ContentType }));
        
        // Combine and sort by creation date
        const allContent = [...books, ...campaigns].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        
        setContent(allContent);
      }
    } catch (err) {
      console.error('Failed to load content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Create book
  const createBook = async (formData: FormData) => {
    if (!session?.access_token) return;

    setCreateLoading(true);
    try {
      const bookData = {
        title: formData.get('title') as string,
        isbn: formData.get('isbn') as string || null,
        status: formData.get('status') as string || 'draft'
      };

      const response = await fetch(`${API_BASE}/api/books`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Erstellen des Buches');
      }

      const newBook = await response.json();
      
      // Then upload cover if provided
      if (coverFile) {
        const coverUrl = await uploadCoverForBook(newBook.id, coverFile);
        if (coverUrl) {
          newBook.cover_url = coverUrl;
        }
      }

      await loadContent();
      setShowCreateModal(false);
      setCoverFile(null);
      setCoverPreview(null);
    } catch (err) {
      console.error('Failed to create book:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Erstellen des Buches');
    } finally {
      setCreateLoading(false);
    }
  };

  // Create campaign
  const createCampaign = async (formData: FormData) => {
    if (!session?.access_token) return;

    setCreateLoading(true);
    try {
      const campaignData = {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        status: formData.get('status') as string || 'draft',
        start_date: formData.get('start_date') as string || null,
        end_date: formData.get('end_date') as string || null,
        target_audience: formData.get('target_audience') as string || null
      };

      const response = await fetch(`${API_BASE}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(campaignData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Erstellen der Kampagne');
      }

      await loadContent();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to create campaign:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Erstellen der Kampagne');
    } finally {
      setCreateLoading(false);
    }
  };

  // Update item
  const updateItem = async (formData: FormData) => {
    if (!session?.access_token || !selectedItem) return;

    setUpdateLoading(true);
    try {
      const endpoint = selectedItem.type === 'book' ? 'books' : 'campaigns';
      
      let updateData: any;
      if (selectedItem.type === 'book') {
        updateData = {
          title: formData.get('title') as string,
          isbn: formData.get('isbn') as string || null,
          status: formData.get('status') as string
        };
      } else {
        updateData = {
          title: formData.get('title') as string,
          description: formData.get('description') as string,
          status: formData.get('status') as string,
          start_date: formData.get('start_date') as string || null,
          end_date: formData.get('end_date') as string || null,
          target_audience: formData.get('target_audience') as string || null
        };
      }

      const response = await fetch(`${API_BASE}/api/${endpoint}/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Aktualisieren');
      }

      await loadContent();
      setShowEditModal(false);
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to update item:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Aktualisieren');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Delete item
  const deleteItem = async (item: ContentItem) => {
    if (!session?.access_token) return;
    
    const itemType = item.type === 'book' ? 'Buch' : 'Kampagne';
    if (!confirm(`Diese ${itemType} wirklich löschen?`)) return;

    setDeleteLoading(item.id);
    try {
      const endpoint = item.type === 'book' ? 'books' : 'campaigns';
      
      const response = await fetch(`${API_BASE}/api/${endpoint}/${item.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Fehler beim Löschen');
      }

      await loadContent();
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert(err instanceof Error ? err.message : 'Fehler beim Löschen');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Filter content
  const filteredContent = content.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.type === 'campaign' && (item as Campaign).description?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = contentFilter === 'all' || item.type === contentFilter;
    return matchesSearch && matchesFilter;
  });

  // Sort content
  const sortedContent = [...filteredContent].sort((a, b) => {
    let aVal: any, bVal: any;
    
    switch (sortField) {
      case 'title':
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      case 'created_at':
        aVal = new Date(a.created_at).getTime();
        bVal = new Date(b.created_at).getTime();
        break;
      default:
        return 0;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Handle sorting
  const handleSort = (field: 'title' | 'status' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get status color
  const getStatusColor = (item: ContentItem) => {
    if (item.type === 'book') {
      switch (item.status) {
        case 'published': return 'bg-green-100 text-green-800';
        case 'draft': return 'bg-gray-100 text-gray-800';
        case 'archived': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else {
      switch ((item as Campaign).status) {
        case 'active': return 'bg-green-100 text-green-800';
        case 'paused': return 'bg-yellow-100 text-yellow-800';
        case 'completed': return 'bg-gray-100 text-gray-800';
        case 'draft': return 'bg-blue-100 text-blue-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
  };

  // Get status label
  const getStatusLabel = (item: ContentItem) => {
    if (item.type === 'book') {
      const bookItem = item as Book;
      switch (bookItem.status) {
        case 'published': return 'Veröffentlicht';
        case 'draft': return 'Entwurf';
        case 'archived': return 'Archiviert';
        default: return bookItem.status;
      }
    } else {
      const campaignItem = item as Campaign;
      switch (campaignItem.status) {
        case 'active': return 'Aktiv';
        case 'paused': return 'Pausiert';
        case 'completed': return 'Abgeschlossen';
        case 'draft': return 'Entwurf';
        default: return campaignItem.status;
      }
    }
  };

  useEffect(() => {
    loadContent();
  }, [session]);

  // Handle query parameters
  useEffect(() => {
    const createParam = searchParams.get('create');
    const editParam = searchParams.get('edit');
    const focusParam = searchParams.get('focus');
    const typeParam = searchParams.get('type');

    if (createParam === 'book' || createParam === 'campaign') {
      console.log('📝 Opening create modal for:', createParam);
      setCreateType(createParam);
      setShowCreateModal(true);
      
      // Clear the query params after opening modal
      navigate(location.pathname, { replace: true });
      
      // Focus on first input after modal opens
      if (focusParam === 'true') {
        setTimeout(() => {
          const firstInput = document.querySelector('#create-content-modal input:not([type="radio"])') as HTMLInputElement;
          if (firstInput) {
            firstInput.focus();
          }
        }, 100);
      }
    } else if (editParam && content.length > 0) {
      // Find and edit the specified item
      const itemToEdit = content.find(c => c.id === editParam);
      if (itemToEdit) {
        console.log('📝 Opening edit modal for:', editParam);
        setSelectedItem(itemToEdit);
        setShowEditModal(true);
        
        // Clear the query params after opening modal
        navigate(location.pathname, { replace: true });
        
        // Focus on first input after modal opens
        if (focusParam === 'true') {
          setTimeout(() => {
            const firstInput = document.querySelector('#edit-content-modal input:not([type="radio"])') as HTMLInputElement;
            if (firstInput) {
              firstInput.focus();
            }
          }, 100);
        }
      }
    }
  }, [searchParams, content, navigate, location.pathname]);

  const handleCreateClick = () => {
    setShowCreateModal(true);
  };

  const bookUsageInfo = getBookUsageInfo ? getBookUsageInfo() : null;
  const campaignUsageInfo = getCampaignUsageInfo ? getCampaignUsageInfo() : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="content-loading">
        <p className="text-gray-500">Lade Inhalte...</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Inhalte verwalten</h1>
          <p className="text-sm text-gray-600 mt-1">
            Bücher und Kampagnen an einem Ort
          </p>
        </div>
        <button
          onClick={handleCreateClick}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          data-testid="content-create"
        >
          <Plus className="w-5 h-5" />
          <span>Neu erstellen</span>
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Inhalte suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="content-search-input"
          />
        </div>
        <select
          value={contentFilter}
          onChange={(e) => setContentFilter(e.target.value as ContentFilter)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="content-filter"
        >
          <option value="all">Alle Inhalte</option>
          <option value="book">Nur Bücher</option>
          <option value="campaign">Nur Kampagnen</option>
        </select>
      </div>

      {/* Content Table */}
      {sortedContent.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {searchTerm || contentFilter !== 'all' 
              ? 'Keine Inhalte gefunden' 
              : 'Noch keine Inhalte erstellt'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-gray-50">
                <TableHead className="text-xs uppercase tracking-wider">
                  Typ
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('title')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Titel
                    {sortField === 'title' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Status
                    {sortField === 'status' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider">
                  Details
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('created_at')}
                    className="flex items-center gap-1 hover:text-gray-700"
                  >
                    Erstellt
                    {sortField === 'created_at' ? (
                      sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-50" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">
                  Aktionen
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedContent.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="whitespace-nowrap">
                    {item.type === 'book' ? (
                      <BookOpen className="w-5 h-5 text-blue-600" />
                    ) : (
                      <Megaphone className="w-5 h-5 text-purple-600" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.title}</div>
                      {item.type === 'campaign' && (item as Campaign).description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {(item as Campaign).description}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(item)}`}>
                      {getStatusLabel(item)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {item.type === 'book' ? (
                      <div className="space-y-1">
                        {(item as Book).isbn && (
                          <div>ISBN: {(item as Book).isbn}</div>
                        )}
                        {(item as Book).cover_url && (
                          <button
                            onClick={() => {
                              setSelectedCover((item as Book).cover_url!);
                              setShowCoverModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            Cover ansehen
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {(item as Campaign).start_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date((item as Campaign).start_date!).toLocaleDateString('de-DE')}
                            {(item as Campaign).end_date && ` - ${new Date((item as Campaign).end_date!).toLocaleDateString('de-DE')}`}
                          </div>
                        )}
                        {(item as Campaign).target_audience && (
                          <div className="flex items-center gap-1">
                            <Target className="w-4 h-4" />
                            {(item as Campaign).target_audience}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => navigate(`/qr/dash/links?${item.type}=${item.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="QR-Codes anzeigen"
                      >
                        <QrCode className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setShowEditModal(true);
                          setCoverFile(null);
                          setCoverPreview(null);
                        }}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Bearbeiten"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteItem(item)}
                        disabled={deleteLoading === item.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div id="create-content-modal" className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Neuen Inhalt erstellen</h2>
            
            {/* Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Was möchten Sie erstellen?
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setCreateType('book')}
                  className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    createType === 'book' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  disabled={!canCreateBook || !canCreateBook()}
                >
                  <BookOpen className="w-8 h-8 text-blue-600" />
                  <span className="font-medium">Buch</span>
                  {bookUsageInfo && (
                    <span className="text-xs text-gray-500">
                      {bookUsageInfo.used} von {bookUsageInfo.limit}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setCreateType('campaign')}
                  className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    createType === 'campaign' 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  disabled={!canCreateCampaign || !canCreateCampaign()}
                >
                  <Megaphone className="w-8 h-8 text-purple-600" />
                  <span className="font-medium">Kampagne</span>
                  {campaignUsageInfo && (
                    <span className="text-xs text-gray-500">
                      {campaignUsageInfo.campaigns_used} von {campaignUsageInfo.campaigns_limit === -1 ? '∞' : campaignUsageInfo.campaigns_limit}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (createType === 'book') {
                if (checkBookLimitAndRedirect && checkBookLimitAndRedirect()) {
                  createBook(new FormData(e.currentTarget));
                }
              } else {
                if (checkCampaignLimitAndRedirect && checkCampaignLimitAndRedirect()) {
                  createCampaign(new FormData(e.currentTarget));
                }
              }
            }}>
              {createType === 'book' ? (
                // Book form fields
                <div className="space-y-4">
                  {/* Cover Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover-Bild
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="w-24 h-36 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                        {coverPreview ? (
                          <img
                            src={coverPreview}
                            alt="Cover preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Camera className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setCoverFile(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setCoverPreview(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="cover-upload-create"
                        />
                        <label
                          htmlFor="cover-upload-create"
                          className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {coverPreview ? 'Cover ändern' : 'Cover hochladen'}
                        </label>
                        {coverPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setCoverFile(null);
                              setCoverPreview(null);
                            }}
                            className="ml-2 text-sm text-red-600 hover:text-red-800"
                          >
                            Entfernen
                          </button>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          JPG, PNG oder GIF. Max. 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ISBN
                    </label>
                    <input
                      type="text"
                      name="isbn"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="draft">Entwurf</option>
                      <option value="published">Veröffentlicht</option>
                      <option value="archived">Archiviert</option>
                    </select>
                  </div>
                </div>
              ) : (
                // Campaign form fields
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel *
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beschreibung
                    </label>
                    <textarea
                      name="description"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="draft">Entwurf</option>
                      <option value="active">Aktiv</option>
                      <option value="paused">Pausiert</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Startdatum
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enddatum
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zielgruppe
                    </label>
                    <input
                      type="text"
                      name="target_audience"
                      placeholder="z.B. Frauen 25-45, Buchliebhaber"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateType('book');
                    setCoverFile(null);
                    setCoverPreview(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {createLoading ? 'Erstelle...' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div id="edit-content-modal" className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedItem.type === 'book' ? 'Buch bearbeiten' : 'Kampagne bearbeiten'}
            </h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              if (selectedItem.type === 'book' && coverFile) {
                // Upload cover if changed
                uploadCoverForBook(selectedItem.id, coverFile).then(() => {
                  updateItem(formData);
                });
              } else {
                updateItem(formData);
              }
            }}>
              {selectedItem.type === 'book' ? (
                // Book edit form with cover upload
                <div className="space-y-4">
                  {/* Cover Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover-Bild
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="w-24 h-36 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                        {coverPreview || (selectedItem as Book).cover_url ? (
                          <img
                            src={coverPreview || (selectedItem as Book).cover_url}
                            alt="Cover preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Camera className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setCoverFile(file);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setCoverPreview(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="cover-upload-edit"
                        />
                        <label
                          htmlFor="cover-upload-edit"
                          className="cursor-pointer inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {coverPreview || (selectedItem as Book).cover_url ? 'Cover ändern' : 'Cover hochladen'}
                        </label>
                        {(coverPreview || (selectedItem as Book).cover_url) && (
                          <button
                            type="button"
                            onClick={() => {
                              setCoverFile(null);
                              setCoverPreview(null);
                            }}
                            className="ml-2 text-sm text-red-600 hover:text-red-800"
                          >
                            Entfernen
                          </button>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          JPG, PNG oder GIF. Max. 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel *
                    </label>
                    <input
                      type="text"
                      name="title"
                      defaultValue={selectedItem.title}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ISBN
                    </label>
                    <input
                      type="text"
                      name="isbn"
                      defaultValue={(selectedItem as Book).isbn}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue={selectedItem.status}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="draft">Entwurf</option>
                      <option value="published">Veröffentlicht</option>
                      <option value="archived">Archiviert</option>
                    </select>
                  </div>
                </div>
              ) : (
                // Campaign edit form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel *
                    </label>
                    <input
                      type="text"
                      name="title"
                      defaultValue={selectedItem.title}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beschreibung
                    </label>
                    <textarea
                      name="description"
                      defaultValue={(selectedItem as Campaign).description}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue={(selectedItem as Campaign).status}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="draft">Entwurf</option>
                      <option value="active">Aktiv</option>
                      <option value="paused">Pausiert</option>
                      <option value="completed">Abgeschlossen</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Startdatum
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        defaultValue={(selectedItem as Campaign).start_date?.split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enddatum
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        defaultValue={(selectedItem as Campaign).end_date?.split('T')[0]}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zielgruppe
                    </label>
                    <input
                      type="text"
                      name="target_audience"
                      defaultValue={(selectedItem as Campaign).target_audience}
                      placeholder="z.B. Frauen 25-45, Buchliebhaber"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedItem(null);
                    setCoverFile(null);
                    setCoverPreview(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={updateLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {updateLoading ? 'Speichere...' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cover Modal */}
      {showCoverModal && selectedCover && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCoverModal(false)}>
          <div className="bg-white rounded-lg p-4 max-w-md max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Buchcover</h3>
              <button
                onClick={() => setShowCoverModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <img
              src={selectedCover}
              alt="Buchcover"
              className="w-full h-auto rounded"
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Content; 
